/**
 * Utility to manage Firebase authentication flags in localStorage
 * Emergency bypass resets have been disabled per user instruction
 */

import {
  isIOSPlatform,
  getCurrentPlatform
} from './platform-detection';
import logger from './logger';

/**
 * We've disabled all emergency reset functionality as it was causing issues
 * The only function we keep is the user ID storage for session consistency
 */
export const resetStorageFlags = () => {
  // All emergency bypass resets have been disabled
  logger.info('Emergency bypass resets disabled per user request');
};

/**
 * Clear all Firebase-related flags from localStorage
 * This provides a clean slate for Firebase authentication
 */
export const clearAllFirebaseFlags = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  // Clear bypass flags
  localStorage.removeItem('BYPASS_LOADING');
  localStorage.removeItem('FORCE_BYPASS');
  localStorage.removeItem('FORCE_DEV_MODE');
  localStorage.removeItem('AUTH_TIMEOUT_OCCURRED');
  localStorage.removeItem('BYPASS_FIREBASE_PERMISSION');

  // Set flag to force real Firebase
  localStorage.setItem('USE_REAL_FIREBASE', 'true');

  // Don't log this in production builds - it causes too much noise
  const platform = getCurrentPlatform();
  if (process.env.NODE_ENV === 'development') {
    logger.info(`Platform detection: Cleared Firebase flags on ${platform} platform`);
  }

  if (isIOSPlatform()) {
    logger.info('iOS platform detected - special handling applied');
    // We could add iOS-specific logic here if needed
  }

  return true;
};

/**
 * Stores the current user's ID in localStorage for recovery purposes
 * This helps ensure we use real Firebase data when a user is authenticated
 */
export const storeAuthUserId = (userId: string | null) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  if (userId) {
    localStorage.setItem('AUTH_USER_ID', userId);
    // Log platform-specific info when storing user ID
    if (isIOSPlatform()) {
      logger.info('Auth user ID stored with iOS detection active');
    }
  } else {
    localStorage.removeItem('AUTH_USER_ID');
  }
};

const exportedObject = {
  resetStorageFlags,
  clearAllFirebaseFlags,
  storeAuthUserId
};

export default exportedObject;